import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Copy, Download, RotateCcw, ArrowLeft } from 'lucide-react';
import { STEPS, FORWARD, ENTRY_POINTS, traceToText } from './traceSteps';
import { Label } from '../Label';

export default function TraceResult({ answers, intensity, onRestart }) {
  const [entry, setEntry] = useState(null);
  const text = traceToText(answers, intensity);

  const copy = async () => { await navigator.clipboard.writeText(text); toast.success('Trace copied'); };
  const download = () => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    a.download = 'trace.txt';
    a.click();
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} data-testid="trace-result">
      <p className="font-mono-precision text-[10px] uppercase tracking-[0.3em] text-cyan-300">Your trace · read forward</p>
      <h2 className="mt-5 font-celestial text-3xl sm:text-4xl text-slate-100 leading-tight">The reaction was not random.<br /><span className="text-cyan-300">It ran a chain.</span></h2>
      <p className="mt-4 text-sm sm:text-base text-slate-400 leading-relaxed max-w-2xl">Here is the sequence you described, in the order it was built. Nothing here is a verdict about you. It is a map of one moment.</p>

      <ol className="mt-10 relative" data-testid="trace-chain">
        <span className="absolute left-[11px] top-3 bottom-3 w-px bg-gradient-to-b from-cyan-400/60 via-cyan-400/20 to-amber-400/60" />
        {FORWARD.map((k, i) => {
          const s = STEPS.find((x) => x.key === k);
          const on = entry === k;
          return (
            <motion.li key={k} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + i * 0.1 }} className="relative pl-10 pb-8" data-testid={`trace-chain-${k}`}>
              <button onClick={() => setEntry(on ? null : k)} className={`absolute left-0 top-1 w-6 h-6 rounded-full border grid place-items-center transition-colors ${on ? 'border-amber-400 bg-amber-400/20' : 'border-cyan-400/50 bg-[#02040a] hover:border-amber-400'}`} aria-label={`Consider ${s.stage} as an entry point`} data-testid={`trace-entry-${k}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${on ? 'bg-amber-300' : 'bg-cyan-300'}`} />
              </button>
              <p className="font-mono-precision text-[10px] uppercase tracking-[0.25em] text-slate-500">{s.stage}{k === 'emotion' ? ` · ${intensity}/10` : ''}</p>
              <p className="mt-1.5 text-base sm:text-lg text-slate-200 leading-relaxed">{answers[k]}</p>
              {on && (
                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mt-4 rounded-2xl border border-amber-400/25 bg-amber-400/[0.04] p-5" data-testid={`trace-entry-note-${k}`}>
                  <p className="font-mono-precision text-[10px] uppercase tracking-[0.25em] text-amber-300 mb-2">A possible entry point</p>
                  <p className="text-sm sm:text-base text-amber-50/85 leading-relaxed">{ENTRY_POINTS[k]}</p>
                </motion.div>
              )}
            </motion.li>
          );
        })}
      </ol>

      <div className="rounded-2xl border border-white/[0.08] p-6" data-testid="trace-entry-prompt">
        <p className="font-celestial text-lg text-slate-100">Where could a new experience enter?</p>
        <p className="mt-2 text-sm text-slate-400 leading-relaxed">Tap any circle on the chain. Each link is a place the sequence could be joined by something new. Chains are not broken; they are added to.</p>
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <button onClick={copy} className="inline-flex items-center gap-2 font-mono-precision text-[10px] uppercase tracking-[0.25em] px-5 py-3 rounded-full border border-white/15 text-slate-300 hover:text-cyan-300 hover:border-cyan-400/50 transition-colors" data-testid="trace-copy-btn"><Copy size={13} /> Copy</button>
        <button onClick={download} className="inline-flex items-center gap-2 font-mono-precision text-[10px] uppercase tracking-[0.25em] px-5 py-3 rounded-full border border-white/15 text-slate-300 hover:text-cyan-300 hover:border-cyan-400/50 transition-colors" data-testid="trace-download-btn"><Download size={13} /> Download</button>
        <button onClick={onRestart} className="inline-flex items-center gap-2 font-mono-precision text-[10px] uppercase tracking-[0.25em] px-5 py-3 rounded-full border border-white/15 text-slate-300 hover:text-amber-300 hover:border-amber-400/50 transition-colors" data-testid="trace-restart-btn"><RotateCcw size={13} /> Trace another</button>
        <Link to="/u99/tools" className="inline-flex items-center gap-2 font-mono-precision text-[10px] uppercase tracking-[0.25em] px-5 py-3 text-slate-500 hover:text-slate-200 transition-colors" data-testid="trace-return-btn"><ArrowLeft size={13} /> Return to tools</Link>
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-4 text-xs text-slate-500 leading-relaxed" data-testid="trace-disclaimer">
        <Label id="psychological-guidance" size="xs" />
        <span>Nothing you typed was sent anywhere. This tool does not diagnose, and it is not a substitute for professional care.</span>
      </div>
    </motion.div>
  );
}
